import { ClientUpdateValues, ClientValues } from "../../../input-types";
import { Client, ClientType, } from "../types";
import { batchFetchItems, getUrl } from "./service-utils";

const getClientURL = (
    parameters?: string | undefined,
) => {

    return getUrl("client", undefined, parameters)
}

const dataToClient = (data: any): Client | null => {
    if (!data) {
        return null
    }

    if (!data.clientId) {
        console.warn("Tried to convert object without clientId", data)
        return null
    }

    const client: Client = {
        clientId: data.clientId,
        clientName: data.clientName || "",
        type: data.type || ClientType.Active,
        organisationIds: data.organisationIds || [],
        created: data.created ? new Date(data.created) : new Date(),
        domainWhiteList: data.domainWhiteList || []
    };

    return client
}

/**
 * Fetches all clients
 * @param authToken - Bearer token for authentication
 * @returns Promise resolving to array of Client objects
 */
const listClients = async (
    authToken: string,
): Promise<Client[]> => {

    const url = getClientURL()

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
            return [];
        }

        return data.map(dataToClient).filter((client: Client | null) => client !== null) as Client[];
    } catch (error) {
        console.error("Error fetching clients:", error);
        return [];
    }
};

/**
 * Fetches a single client by ID
 * @param authToken - Bearer token for authentication
 * @param targetClientId - The ID of the client to fetch
 * @returns Promise resolving to a Client object or null
 */
const getClient = async (
    authToken: string,
    targetClientId: string,
): Promise<Client | null> => {

    const url = getClientURL(targetClientId)

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            if (response.status === 404) {
                return null;
            }
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        const clients: Client[] = data.map(dataToClient).filter((item: Client | null) => item != null);

        if (clients.length == 0) {
            console.error("Client data was missing")
            return null
        }

        return clients[0]
    } catch (error) {
        console.error("Error fetching client:", error);
        return null;
    }
};

/**
 * Creates a new client
 * @param authToken - Bearer token for authentication (DeveloperAdmin required)
 * @param client - Object containing clientName
 * @param clientId - The id for the client partition
 * @returns Promise resolving to the created Client object
 */
const addClient = async (
    authToken: string,
    input: ClientValues,
    clientId?: string | undefined,
): Promise<Client | null> => {

    const url = getClientURL(clientId)

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(input),
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        return dataToClient(data)
    } catch (error) {
        console.error("Error creating client:", error);
        throw error as Error;
    }
};

/**
 * Updates an existing client
 * @param authToken - Bearer token for authentication
 * @param clientId - The ID of the client to update
 * @param client - Updated client
 * @param clientId - The id for the client partition
 * @returns Promise resolving to the updated Client object
 */
const updateClient = async (
    authToken: string,
    clientId: string,
    client: ClientUpdateValues,
): Promise<Client | null> => {

    const url = getClientURL(clientId)

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(client),
        });

        if (!response.ok) {
            throw new Error("Network response was not ok");
        }

        const data = await response.json();
        return dataToClient(data)
    } catch (error) {
        console.error("Error updating client:", error);
        throw error as Error;
    }
};

/**
 * Fetches clients by ID
 * @param authToken - Bearer token for authentication
 * @param clientIds - The IDs of the client to fetch
 * @returns Promise resolving to a Client object or null
 */
const getClientsWithIds = async (
    authToken: string,
    clientIds: string[],
): Promise<(Client | null)[]> => {

    return batchFetchItems(
        authToken,
        clientIds,
        getClientURL,
        dataToClient,
        undefined,
    )
}

const ClientAPIService = {
    listClients,
    getClient,
    addClient,
    updateClient,
    getClientsWithIds,
};

export default ClientAPIService;